import * as CANNON from 'cannon-es'
import type { LotteryMachineConfig, MachineId, BallEntity } from '../core/types'
import {
  createBalls,
  releaseBallsFromTubes,
  rescueBallsStillInRack,
  type BallBodyBundle,
} from './BallFactory'
import { GateController } from './GateController'
import { exitRollEase, sampleExitPath } from './MachineLayout'
import { MixerController } from './MixerController'
import { createPhysicsWorld, type PhysicsHandles } from './PhysicsWorldFactory'

export class MachineWorld {
  readonly handles: PhysicsHandles
  readonly mixer: MixerController
  readonly gates: GateController
  readonly bundles: BallBodyBundle[]
  private readonly bodyById = new Map<string, CANNON.Body>()
  private readonly entityById = new Map<string, BallEntity>()
  private capturedIds = new Set<string>()
  private readonly captureOrder: string[] = []
  /** 管道滚动：上一帧位置，用于累计无滑动旋转 */
  private readonly railPrevPos = new Map<string, CANNON.Vec3>()
  /** 已停靠的最终管位，每帧锁定防漂移 */
  private readonly railFinalPos = new Map<string, CANNON.Vec3>()
  private now = 0
  private tubesReleased = false
  private rackRescueDone = false
  private releaseAt = -1

  constructor(
    readonly machineId: MachineId,
    readonly config: LotteryMachineConfig,
  ) {
    const displaySlots = machineId === 'red' ? config.redCount : config.blueCount
    this.handles = createPhysicsWorld(config, displaySlots)
    this.mixer = new MixerController(this.handles.diskInner, this.handles.diskOuter)
    this.gates = new GateController(
      this.handles.gateExit,
      this.handles.gatePreview,
      this.handles.gateExit.position.clone(),
      this.handles.gatePreview.position.clone(),
    )
    this.gates.setPreviewReleaseHandler(() => this.releaseFeedTubes())

    const pool = machineId === 'red' ? config.redPoolSize : config.bluePoolSize
    this.bundles = createBalls(
      this.handles.world,
      machineId,
      pool,
      config,
      this.handles.layout.feedTubes,
    )
    for (const b of this.bundles) {
      this.bodyById.set(b.entity.id, b.body)
      this.entityById.set(b.entity.id, b.entity)
    }
  }

  releaseFeedTubes(): void {
    if (this.tubesReleased) return
    this.tubesReleased = true
    this.releaseAt = this.now
    this.rackRescueDone = false
    releaseBallsFromTubes(
      this.bundles,
      this.handles.layout,
      this.config.ballRadius,
    )
  }

  getEntities(): BallEntity[] {
    return this.bundles.map((b) => b.entity)
  }

  getBody(id: string): CANNON.Body | undefined {
    return this.bodyById.get(id)
  }

  getEntity(id: string): BallEntity | undefined {
    return this.entityById.get(id)
  }

  step(dt: number): void {
    this.now += dt
    this.mixer.update(dt, this.now)
    this.driveBallsOnTurntables(dt)

    // 开闸约 0.8s 后仍卡在顶管的球，一次性救援（不是每帧乱拉）
    if (
      this.tubesReleased &&
      !this.rackRescueDone &&
      this.releaseAt >= 0 &&
      this.now - this.releaseAt > 0.8
    ) {
      rescueBallsStillInRack(
        this.bundles,
        this.handles.layout.feedTubes,
        this.config.ballRadius,
      )
      this.rackRescueDone = true
    }

    if (this.gates.isExitOpen()) {
      const c = this.handles.exitSensorCenter
      for (const { body, entity } of this.bundles) {
        if (entity.state !== 'in_chamber') continue
        if (this.capturedIds.has(entity.id)) continue
        const dx = c.x - body.position.x
        const dz = c.z - body.position.z
        const dist = Math.hypot(dx, dz)
        if (dist > 0.35 || dist < 1e-4) continue
        // 只水平吸向出球口，避免把球往下扯进孔里乱晃
        const s = 0.02 / dist
        body.applyForce(new CANNON.Vec3(dx * s, 0, dz * s), body.position)
      }
    }

    this.handles.world.step(dt)
    this.preventFallThroughDisk()
    this.rescueBallsFallenThroughExit()
    this.stirChamberBalls(dt)
    this.knockDownHoveringBalls()
    this.containChamberBalls()
    this.containChamberBalls()
    this.lockCapturedBalls()
  }

  /**
   * 仅救援「贴顶几乎不动」的卡球；不再把中上层正常翻滚球强行拉下。
   */
  private knockDownHoveringBalls(): void {
    if (!this.tubesReleased) return
    const cy = this.handles.layout.chamberCenterY
    const R = this.handles.chamberRadius
    // 接近球顶且几乎无下落速度才干预
    const stuckY = cy + R * 0.72

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber' && entity.state !== 'rack') continue
      if (this.capturedIds.has(entity.id)) continue
      if (body.type === CANNON.Body.KINEMATIC) continue

      const p = body.position
      if (p.y < stuckY) continue

      const speed = Math.hypot(body.velocity.x, body.velocity.y, body.velocity.z)
      if (speed < 0.35 && body.velocity.y > -0.4) {
        body.velocity.x *= 0.5
        body.velocity.z *= 0.5
        body.velocity.y = -2.5
        body.wakeUp()
      }
    }
  }

  /** 防穿盘：含出球孔柱，未捕获的球不得掉进底部空腔乱晃 */
  private preventFallThroughDisk(): void {
    const diskTop = this.handles.diskTopY
    const rBall = this.config.ballRadius
    const minY = diskTop + rBall
    const maxR = this.handles.layout.floorRadius + 0.05

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber' && entity.state !== 'rack') continue
      if (this.capturedIds.has(entity.id)) continue
      if (body.type === CANNON.Body.KINEMATIC) continue
      const p = body.position
      if (Math.hypot(p.x, p.z) > maxR) continue
      if (p.y < minY) {
        p.y = minY
        if (body.velocity.y < 0) body.velocity.y = 0
      }
    }
  }

  /** 已掉进出球孔/管腔的未捕获球：拉回盘面 */
  private rescueBallsFallenThroughExit(): void {
    const diskTop = this.handles.diskTopY
    const r = this.config.ballRadius
    const exit = this.handles.layout.exitHoleCenter
    const hole = this.handles.layout.exitHoleRadius

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber') continue
      if (this.capturedIds.has(entity.id)) continue
      if (body.type === CANNON.Body.KINEMATIC) continue

      const p = body.position
      if (p.y >= diskTop - r * 0.2) continue

      const awayX = p.x - exit.x || 0.03
      const awayZ = p.z - exit.z || 0.03
      const alen = Math.hypot(awayX, awayZ)
      p.x = exit.x + (awayX / alen) * (hole * 1.8)
      p.z = exit.z + (awayZ / alen) * (hole * 1.8)
      p.y = diskTop + r
      body.velocity.set(0, 0, 0)
      body.angularVelocity.set(0, 0, 0)
      body.wakeUp()
    }
  }

  /**
   * 满舱搅拌扰动：中上层弱扰动；力度收紧，避免冲出球壳。
   */
  private stirChamberBalls(dt: number): void {
    const omega = this.mixer.getOmega()
    if (omega < 2) return

    const diskTop = this.handles.diskTopY
    const r = this.config.ballRadius
    const cy = this.handles.layout.chamberCenterY
    const strength = Math.min(1, omega / this.config.diskOmegaTarget) * 0.28
    const t = this.now

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber') continue
      if (this.capturedIds.has(entity.id)) continue
      if (body.type === CANNON.Body.KINEMATIC) continue

      const p = body.position
      if (p.y < diskTop + r * 1.4) continue
      if (p.y > cy + this.handles.chamberRadius * 0.65) continue

      const idHash = entity.number * 12.9898 + entity.id.length
      const n1 = Math.sin(t * 7.1 + idHash)
      const n2 = Math.cos(t * 5.3 + idHash * 1.7)
      const n3 = Math.sin(t * 4.2 + idHash * 0.6)
      const f = 0.06 * strength * body.mass / Math.max(dt, 1 / 120)
      body.applyForce(
        new CANNON.Vec3(n1 * f, Math.abs(n3) * f * 0.55, n2 * f),
        p,
      )
      body.wakeUp()
    }
  }

  /** 盘面摩擦驱动 + 有限掀起（盘径不改） */
  private driveBallsOnTurntables(dt: number): void {
    const omega = this.mixer.getOmega()
    if (omega < 0.1) return

    const layout = this.handles.layout
    const rBall = this.config.ballRadius
    const blend = Math.min(0.5, 5 * dt)
    const expectedY = this.handles.diskTopY + rBall
    const outerOut = layout.outerDiskOuterR
    const launch = Math.min(1, omega / Math.max(1, this.config.diskOmegaTarget))

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber') continue
      if (this.capturedIds.has(entity.id)) continue

      const p = body.position
      const radial = Math.hypot(p.x, p.z)
      if (radial > outerOut + 0.02) continue
      if (p.y > expectedY + rBall * 0.85) continue
      if (p.y < expectedY - rBall * 0.5) continue

      let sign = 1
      if (radial >= layout.outerDiskInnerR) sign = -1
      else if (radial > layout.innerDiskRadius) {
        const t =
          (radial - layout.innerDiskRadius) /
          Math.max(1e-4, layout.outerDiskInnerR - layout.innerDiskRadius)
        sign = 1 - 2 * t
      }

      const speed = omega * Math.max(radial, 0.02) * sign * 1.08
      const inv = 1 / Math.max(radial, 1e-4)
      const tx = -p.z * inv
      const tz = p.x * inv
      body.velocity.x += (tx * speed - body.velocity.x) * blend
      body.velocity.z += (tz * speed - body.velocity.z) * blend

      const phase = (entity.number * 1.7 + this.now * omega * 0.35) % (Math.PI * 2)
      if (phase < 0.35 && body.velocity.y < 0.8) {
        body.velocity.y = Math.min(
          2.4,
          body.velocity.y + (1.6 + launch * 1.2) * blend * 2.4,
        )
      }

      const vh = Math.hypot(body.velocity.x, body.velocity.z)
      const vmax = 3.2 + launch * 1.2
      if (vh > vmax) {
        const s = vmax / vh
        body.velocity.x *= s
        body.velocity.z *= s
      }

      body.wakeUp()
    }
  }

  /**
   * 强制留在玻璃球内。仅「仍在顶管内的 rack 球」放宽；
   * 已入舱的球一律夹紧（修复飞到管口高度后逃逸）。
   */
  private containChamberBalls(): void {
    const cy = this.handles.layout.chamberCenterY
    const R = this.handles.chamberRadius
    const r = this.config.ballRadius
    const maxDist = Math.max(0.04, (R - r) * 0.992)
    const exit = this.handles.layout.exitHoleCenter
    const hole = this.handles.layout.exitHoleRadius
    const exitOpen = this.gates.isExitOpen()
    const diskTop = this.handles.diskTopY
    const tubes = this.handles.layout.feedTubes

    for (const { body, entity } of this.bundles) {
      if (entity.state !== 'in_chamber' && entity.state !== 'rack') continue
      if (this.capturedIds.has(entity.id)) continue
      if (body.type === CANNON.Body.KINEMATIC) continue

      const p = body.position
      const nearHole = Math.hypot(p.x - exit.x, p.z - exit.z) < hole * 1.4

      if (!exitOpen && nearHole && p.y < diskTop + r * 2.2) {
        const awayX = p.x - exit.x || 0.02
        const awayZ = p.z - exit.z || 0.02
        const alen = Math.hypot(awayX, awayZ)
        p.x = exit.x + (awayX / alen) * (hole * 1.6)
        p.z = exit.z + (awayZ / alen) * (hole * 1.6)
        p.y = Math.max(p.y, diskTop + r)
        if (body.velocity.y < 0) body.velocity.y = 0
      }

      if (entity.state === 'rack') {
        let inTube = false
        for (const tube of tubes) {
          if (
            p.y > tube.bottomY - r * 1.5 &&
            Math.hypot(p.x - tube.x, p.z - tube.z) < tube.radius * 1.35
          ) {
            inTube = true
            break
          }
        }
        if (inTube) continue
      }

      // 未捕获球一律不放行掉出球壳（出球靠盘面传感器捕获后走运动学管道）
      const dx = p.x
      const dy = p.y - cy
      const dz = p.z
      const dist = Math.hypot(dx, dy, dz)
      if (dist < 1e-6) continue

      if (dist > maxDist) {
        const s = maxDist / dist
        p.x = dx * s
        p.y = cy + dy * s
        p.z = dz * s

        const nx = dx / dist
        const ny = dy / dist
        const nz = dz / dist
        const vn = body.velocity.x * nx + body.velocity.y * ny + body.velocity.z * nz
        if (vn > 0) {
          body.velocity.x -= nx * vn * 1.15
          body.velocity.y -= ny * vn * 1.15
          body.velocity.z -= nz * vn * 1.15
        }
      }

      const speed = Math.hypot(body.velocity.x, body.velocity.y, body.velocity.z)
      if (speed > 4.5) {
        const s = 4.5 / speed
        body.velocity.x *= s
        body.velocity.y *= s
        body.velocity.z *= s
      }
    }
  }

  getSimTime(): number {
    return this.now
  }

  markInChamber(): void {
    for (const b of this.bundles) {
      if (b.entity.state === 'rack') b.entity.state = 'in_chamber'
      // 确保落球后都是动力学体
      if (b.body.type === CANNON.Body.KINEMATIC && !this.capturedIds.has(b.entity.id)) {
        b.body.type = CANNON.Body.DYNAMIC
        b.body.mass = b.entity.mass
        b.body.updateMassProperties()
        b.body.wakeUp()
      }
    }
  }

  pollExitCandidates(allow: (ball: BallEntity) => boolean): BallEntity | null {
    if (!this.gates.isExitOpen()) return null
    const c = this.handles.exitSensorCenter
    const h = this.handles.exitSensorHalfExtents

    let best: BallEntity | null = null
    let bestDist = Infinity

    for (const bundle of this.bundles) {
      const { entity, body } = bundle
      if (this.capturedIds.has(entity.id)) continue
      if (entity.state !== 'in_chamber' && entity.state !== 'exiting') continue
      if (!allow(entity)) {
        if (this.inSensor(body.position, c, h)) {
          body.velocity.x = (body.position.x - c.x || 0.05) * 2
          body.velocity.z = (body.position.z - c.z || 0.05) * 2
          body.velocity.y = Math.abs(body.velocity.y) + 0.35
          body.position.y = Math.max(body.position.y, c.y + h.y + 0.02)
        }
        continue
      }

      if (this.inSensor(body.position, c, h)) {
        const d = body.position.distanceTo(c)
        if (d < bestDist) {
          bestDist = d
          best = entity
        }
      }
    }
    return best
  }

  private inSensor(p: CANNON.Vec3, c: CANNON.Vec3, h: CANNON.Vec3): boolean {
    return (
      Math.abs(p.x - c.x) <= h.x &&
      Math.abs(p.y - c.y) <= h.y &&
      Math.abs(p.z - c.z) <= h.z
    )
  }

  nudgeTowardExit(ballId: string): void {
    const body = this.bodyById.get(ballId)
    if (!body) return
    const target = this.handles.exitSensorCenter
    const dir = new CANNON.Vec3(
      target.x - body.position.x,
      target.y - body.position.y,
      target.z - body.position.z,
    )
    const len = dir.length()
    if (len < 1e-4 || len > 0.45) return
    dir.scale(0.4 / len, dir)
    body.applyImpulse(dir, body.position)
  }

  captureBall(ballId: string): void {
    const body = this.bodyById.get(ballId)
    const entity = this.entityById.get(ballId)
    if (!body || !entity) return
    this.capturedIds.add(ballId)
    this.captureOrder.push(ballId)
    entity.state = 'exiting'
    body.type = CANNON.Body.KINEMATIC
    body.collisionResponse = false
    body.velocity.set(0, 0, 0)
    body.angularVelocity.set(0, 0, 0)
    this.railPrevPos.set(ballId, body.position.clone())
  }

  /** 沿 U 管滚动到左侧停位：弧长路径 + 无滑动滚动姿态 */
  animateCapturedToDisplay(ballId: string, t: number): void {
    const body = this.bodyById.get(ballId)
    const entity = this.entityById.get(ballId)
    if (!body || !entity) return
    const k = Math.min(1, Math.max(0, t))
    entity.state = k >= 1 ? 'displayed' : 'on_rail'
    body.type = CANNON.Body.KINEMATIC
    body.collisionResponse = false
    body.velocity.set(0, 0, 0)
    body.angularVelocity.set(0, 0, 0)

    const slotIndex = Math.max(0, this.captureOrder.indexOf(ballId))
    const pos = sampleExitPath(this.handles.layout, slotIndex, exitRollEase(k))

    const prev = this.railPrevPos.get(ballId) ?? body.position.clone()
    const dx = pos.x - prev.x
    const dy = pos.y - prev.y
    const dz = pos.z - prev.z
    const dist = Math.hypot(dx, dy, dz)
    if (dist > 1e-6 && k < 1) {
      const moveX = dx / dist
      const moveZ = dz / dist
      let ax = moveZ
      let ay = 0
      let az = -moveX
      let alen = Math.hypot(ax, ay, az)
      if (alen < 1e-5) {
        ax = 1
        ay = 0
        az = 0
        alen = 1
      }
      ax /= alen
      ay /= alen
      az /= alen
      const angle = dist / this.config.ballRadius
      const dq = new CANNON.Quaternion()
      dq.setFromAxisAngle(new CANNON.Vec3(ax, ay, az), angle)
      body.quaternion = dq.mult(body.quaternion)
    }

    body.position.copy(pos)
    this.railPrevPos.set(ballId, pos.clone())
    if (k >= 1) {
      this.railFinalPos.set(ballId, pos.clone())
      body.angularVelocity.set(0, 0, 0)
    }
  }

  /** 已出球：每帧锁在管道路径/停位，避免被物理步进带跑 */
  private lockCapturedBalls(): void {
    for (const id of this.capturedIds) {
      const body = this.bodyById.get(id)
      const entity = this.entityById.get(id)
      if (!body || !entity) continue
      body.type = CANNON.Body.KINEMATIC
      body.collisionResponse = false
      body.velocity.set(0, 0, 0)
      body.angularVelocity.set(0, 0, 0)
      const parked = this.railFinalPos.get(id)
      if (entity.state === 'displayed' && parked) {
        body.position.copy(parked)
      }
    }
  }

  dispose(): void {
    const { world } = this.handles
    for (const b of [...world.bodies]) {
      world.removeBody(b)
    }
  }
}
